import { Fragment, useLayoutEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";

import { apiFetch } from "@/lib/api";
import { INGEST_CONTROL_QUERY_LIVE, type IngestControlListResponse } from "@/lib/ingest-control-api";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge, type BadgeTone } from "@/components/ui/Badge";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { useErpIngestLiveQueries } from "@/lib/useErpIngestLiveQueries";
import { HoverHintContent, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { formatTimestamp } from "@/lib/utils";

const DATA_CONNECTORS_READ_ONLY_TITLE = "Your role has read-only access to the Data connectors hub module.";

/** Matches `INGEST_EVENT_RUN_LEVEL_MV_ID` in the integrator; whole-run failure, not one MV record. */
const INGEST_EVENT_RUN_LEVEL_MV_ID = "__run__";

function formatMvIdForDisplay(mvId: string): string {
  if (mvId === INGEST_EVENT_RUN_LEVEL_MV_ID) {
    return "Entire run";
  }
  return mvId;
}

const INGEST_MODES = [
  "all",
  "live",
  "dry_run",
  "diff",
  "staging",
  "replay",
  "sweep_report",
  "sweep_apply",
  "manual",
] as const;

const OUTCOMES = ["problems", "success", "all"] as const;
type Outcome = (typeof OUTCOMES)[number];

type IngestRunMode = Exclude<(typeof INGEST_MODES)[number], "all">;

type IngestEventStatus =
  | "failed"
  | "drift"
  | "missing"
  | "orphan"
  | "drift_fixed"
  | "upserted"
  | "unchanged"
  | "skipped";

const PROBLEM_STATUSES: IngestEventStatus[] = ["failed", "drift", "missing", "orphan"];
const SUCCESS_STATUSES: IngestEventStatus[] = ["upserted", "unchanged", "skipped", "drift_fixed"];

/** From GET /api/ingest-events; supplier orphans may include merge heuristics. */
type IngestEventFindingHint = {
  lead: string;
  detail: string;
  recommended_merge_targets: { target_mv_id: string; candidate_name: string; match_score: number }[];
};

interface IngestEvent {
  id: number;
  run_id: number;
  /** Present when the API joins `ingest_run` (newer build). */
  run_mode?: string | null;
  entity: string;
  mv_id: string;
  status: IngestEventStatus;
  error: string | null;
  error_class: string | null;
  payload: unknown;
  diff: unknown;
  attempt: number;
  created_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
  /** Present for supplier + orphan: API ranks similar names (Megaventory merge lead). */
  finding_hint?: IngestEventFindingHint;
}

function filterEventsByOutcome(events: IngestEvent[], outcome: Outcome): IngestEvent[] {
  if (outcome === "all") return events;
  if (outcome === "problems") {
    return events.filter((e) => PROBLEM_STATUSES.includes(e.status));
  }
  return events.filter((e) => SUCCESS_STATUSES.includes(e.status));
}

function statusTone(status: IngestEvent["status"]): BadgeTone {
  switch (status) {
    case "failed":
      return "danger";
    case "drift":
    case "missing":
      return "warning";
    case "orphan":
      return "info";
    case "drift_fixed":
    case "upserted":
      return "success";
    default:
      return "neutral";
  }
}

function sweepDriftAnnotationsFromDiff(diff: unknown): string[] {
  if (diff == null || typeof diff !== "object" || Array.isArray(diff)) {
    return [];
  }
  const raw = (diff as Record<string, unknown>).sweep_drift_annotations;
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter((x): x is string => typeof x === "string");
}

const PURCHASE_INVOICE_FAMILY_ENTITIES = new Set(["gipi", "tsh"]);

/** Present on sweep drift JSON from **`PurchaseInvoiceFamilyIngest`** (see **`gipi.ts`**). */
function driftFindingCategoryFromDiff(entity: string, diff: unknown): string | null {
  if (!PURCHASE_INVOICE_FAMILY_ENTITIES.has(entity)) {
    return null;
  }
  if (diff == null || typeof diff !== "object" || Array.isArray(diff)) {
    return null;
  }
  const raw = (diff as Record<string, unknown>).drift_finding_category;
  return typeof raw === "string" && raw.trim() !== "" ? raw.trim() : null;
}

function driftFindingCategoryLabel(category: string): string {
  if (category === "gipi_to_po_link") {
    return "GIPI to PO link";
  }
  if (category === "gipi_mv_alignment") {
    return "MV alignment";
  }
  return category;
}

function formatRunMode(mode: string | undefined | null): string {
  if (mode == null || mode === "") {
    return "—";
  }
  const labels: Record<string, string> = {
    live: "Live",
    dry_run: "Dry run",
    diff: "Diff",
    staging: "Staging",
    replay: "Replay",
    sweep_report: "Sweep report (pull + classify drift)",
    sweep_apply: "Sweep apply (pull + fix drift)",
    manual: "Manual",
  };
  return labels[mode] ?? mode;
}

function isResolvableStatus(status: IngestEvent["status"]): boolean {
  return status === "failed" || status === "drift" || status === "missing" || status === "orphan";
}

function runIdToNumber(runId: number | string): number {
  if (typeof runId === "number" && !Number.isNaN(runId)) {
    return runId;
  }
  const n = Number.parseInt(String(runId), 10);
  return Number.isFinite(n) ? n : 0;
}

function resolveIngestModeForEvent(
  event: IngestEvent,
  runModeByRunId: ReadonlyMap<number, string>,
): string | null {
  const fromRow = event.run_mode != null && String(event.run_mode).trim() !== "" ? String(event.run_mode).trim() : null;
  if (fromRow) {
    return fromRow;
  }
  const fromLookup = runModeByRunId.get(runIdToNumber(event.run_id));
  return fromLookup != null && fromLookup !== "" ? fromLookup : null;
}

function isDryRunSimulatedSuccess(event: IngestEvent, effectiveMode: string | null): boolean {
  return effectiveMode === "dry_run" && (event.status === "upserted" || event.status === "unchanged");
}

function findingsPageCopy(outcome: Outcome): { title: string; description: string; listLabel: string } {
  if (outcome === "problems") {
    return {
      title: "Findings",
      description:
        "Open items that need attention: failed ingests, drift, missing, and orphans. For supplier sweep orphan rows, the API can add a hint when another supplier has a similar name (Megaventory merge is a common cause), with suggested merge targets. Always confirm the surviving record in Megaventory before re-pointing data. For GIPI/TSH purchase-invoice drift, a badge may show GIPI to PO link (resolver backlog: unlinked purchase order line items or purchase order item id churn on otherwise matching lines) versus MV alignment (other Megaventory header or line-shape drift). Expand Details for sweep notes and the raw diff. Run-level failures (before any record was processed) appear with MV id “Entire run”. Resolve after you fix the cause, or use Replay for per-record failures.",
      listLabel: "items needing attention",
    };
  }
  if (outcome === "success") {
    return {
      title: "Successful ingest events",
      description: "Per-record outcomes that look healthy (for example after a live run or a dry run simulation). Nothing here requires Resolve.",
      listLabel: "successful events",
    };
  }
  return {
    title: "Ingest events",
    description: "All per-record events matching your filters. Use outcome and run type to focus on what you care about.",
    listLabel: "events",
  };
}

export function Findings() {
  const qc = useQueryClient();
  const hubWritesAllowed = useHubModuleWrite("erp");
  useErpIngestLiveQueries();
  const [searchParams] = useSearchParams();
  const [activeEntity, setActiveEntity] = useState<string | "all">("all");
  const [outcome, setOutcome] = useState<Outcome>("problems");

  useLayoutEffect(() => {
    const entity = searchParams.get("entity");
    if (entity && entity.trim() !== "") {
      setActiveEntity(entity.trim());
    }
    const o = searchParams.get("outcome");
    if (o === "problems" || o === "success" || o === "all") {
      setOutcome(o);
    }
  }, [searchParams]);
  const [runMode, setRunMode] = useState<(typeof INGEST_MODES)[number]>("all");
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const pageCopy = findingsPageCopy(outcome);

  const eventsQuery = useQuery({
    queryKey: [
      "ingest-events",
      { unresolved: true, entity: activeEntity, outcome, runMode: runMode === "all" ? null : runMode },
    ],
    queryFn: () =>
      apiFetch<{ events: IngestEvent[] }>("/api/ingest-events", {
        query: {
          unresolved: "true",
          outcome,
          entity: activeEntity === "all" ? undefined : activeEntity,
          run_mode: runMode === "all" ? undefined : (runMode as IngestRunMode),
          limit: 200,
        },
      }),
  });

  const rawEvents = eventsQuery.data?.events ?? [];
  const displayEvents = useMemo(() => filterEventsByOutcome(rawEvents, outcome), [rawEvents, outcome]);
  const responseIncludedNonMatchingOutcome = useMemo(() => {
    if (rawEvents.length === 0) return false;
    if (outcome === "all") return false;
    return filterEventsByOutcome(rawEvents, outcome).length < rawEvents.length;
  }, [rawEvents, outcome]);

  const runIdsForLookupKey = useMemo(() => {
    const set = new Set<number>();
    for (const e of rawEvents) {
      const n = runIdToNumber(e.run_id);
      if (n > 0) {
        set.add(n);
      }
    }
    return [...set].sort((left, right) => left - right).join(",");
  }, [rawEvents]);

  const runsForEventsQuery = useQuery({
    queryKey: ["ingest-runs", "for-events", runIdsForLookupKey],
    queryFn: () =>
      apiFetch<{ runs: { id: number; mode: string }[] }>("/api/ingest-runs", {
        query: { ids: runIdsForLookupKey },
      }),
    enabled: runIdsForLookupKey.length > 0,
    staleTime: 15_000,
  });

  const runModeByRunId = useMemo(() => {
    const map = new Map<number, string>();
    for (const run of runsForEventsQuery.data?.runs ?? []) {
      const id = runIdToNumber(run.id);
      if (id > 0) {
        map.set(id, String(run.mode));
      }
    }
    return map;
  }, [runsForEventsQuery.data?.runs]);

  const entitiesQuery = useQuery({
    queryKey: ["ingest-control"],
    queryFn: () => apiFetch<IngestControlListResponse>("/api/ingest-control"),
    ...INGEST_CONTROL_QUERY_LIVE,
  });

  const resolveMutation = useMutation({
    mutationFn: ({ id, note }: { id: number; note?: string }) =>
      apiFetch(`/api/ingest-events/${id}/resolve`, { method: "POST", body: { note } }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["ingest-events"] });
      void qc.invalidateQueries({ queryKey: ["ingest-runs"] });
      void qc.invalidateQueries({ queryKey: ["ingest-control"] });
    },
  });

  const replayMutation = useMutation({
    mutationFn: ({ entity, mvIds }: { entity: string; mvIds: string[] }) =>
      apiFetch("/api/replay", { method: "POST", body: { entity, mv_ids: mvIds } }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["ingest-events"] });
      void qc.invalidateQueries({ queryKey: ["ingest-runs"] });
      void qc.invalidateQueries({ queryKey: ["ingest-control"] });
    },
  });

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">{pageCopy.title}</h1>
        <p className="text-sm text-slate-600">{pageCopy.description}</p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Filter</CardTitle>
        </CardHeader>
        <CardBody>
          <div className="space-y-3">
            <div>
              <div className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Entity</div>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant={activeEntity === "all" ? "primary" : "secondary"}
                  onClick={() => setActiveEntity("all")}
                >
                  All
                </Button>
                {entitiesQuery.data?.entities.map((entity) => (
                  <Button
                    key={entity.entity}
                    size="sm"
                    variant={activeEntity === entity.entity ? "primary" : "secondary"}
                    onClick={() => setActiveEntity(entity.entity)}
                  >
                    {entity.entity}
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <div className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Outcome</div>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant={outcome === "problems" ? "primary" : "secondary"}
                  onClick={() => setOutcome("problems")}
                >
                  Needs attention
                </Button>
                <Button
                  size="sm"
                  variant={outcome === "success" ? "primary" : "secondary"}
                  onClick={() => setOutcome("success")}
                >
                  Successful
                </Button>
                <Button size="sm" variant={outcome === "all" ? "primary" : "secondary"} onClick={() => setOutcome("all")}>
                  All
                </Button>
              </div>
            </div>
            <div>
              <div className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Run type</div>
              <div className="max-w-sm">
                <select
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900"
                  value={runMode}
                  onChange={(e) => {
                    setRunMode(e.target.value as (typeof INGEST_MODES)[number]);
                  }}
                >
                  {INGEST_MODES.map((m) => (
                    <option key={m} value={m}>
                      {m === "all" ? "All run types" : formatRunMode(m)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            {displayEvents.length} {pageCopy.listLabel}
            {outcome === "problems" ? " (unresolved)" : ""}
            {responseIncludedNonMatchingOutcome && rawEvents.length !== displayEvents.length
              ? ` (filtered from ${rawEvents.length} returned)`
              : ""}
          </CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {responseIncludedNonMatchingOutcome ? (
            <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-950">
              The list below hides events that do not match the selected outcome, because the server sent a full batch
              (for example, successful rows while &quot;Needs attention&quot; is selected). This usually means the
              integrator API is an older build that does not support the <code className="rounded bg-amber-100 px-0.5">outcome</code> and{" "}
              <code className="rounded bg-amber-100 px-0.5">run_mode</code> query. Rebuild and redeploy the API so
              filtering and run type are applied in the server response.
            </div>
          ) : null}
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 font-medium">When</th>
                <th className="px-4 py-2 font-medium">Entity</th>
                <th className="px-4 py-2 font-medium">MV id</th>
                <th className="px-4 py-2 font-medium">Run type</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Error</th>
                <th className="px-4 py-2 font-medium">Run</th>
                <th className="px-4 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {displayEvents.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-6 text-center text-slate-500">
                    No events match the current filters.
                  </td>
                </tr>
              ) : null}
              {displayEvents.map((event) => {
                const effectiveRunMode = resolveIngestModeForEvent(event, runModeByRunId);
                const sweepNotes = sweepDriftAnnotationsFromDiff(event.diff);
                const driftCategory = driftFindingCategoryFromDiff(event.entity, event.diff);
                return (
                <Fragment key={event.id}>
                  <tr className="hover:bg-slate-50">
                    <td className="px-4 py-2 text-slate-600">{formatTimestamp(event.created_at)}</td>
                    <td className="px-4 py-2">
                      <Link to={`/erp/entity/${event.entity}`} className="text-brand-700 hover:underline">
                        {event.entity}
                      </Link>
                    </td>
                    <td className="px-4 py-2 font-mono text-xs">
                      {event.mv_id === INGEST_EVENT_RUN_LEVEL_MV_ID ? (
                        <HoverHintContent
                          hoverHint="This run failed before any per-record event was written."
                          className="inline-flex"
                        >
                          {formatMvIdForDisplay(event.mv_id)}
                        </HoverHintContent>
                      ) : (
                        formatMvIdForDisplay(event.mv_id)
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-700">{formatRunMode(effectiveRunMode)}</td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap items-center gap-1">
                        <Badge tone={statusTone(event.status)}>{event.status}</Badge>
                        {isDryRunSimulatedSuccess(event, effectiveRunMode) ? (
                          <span className="text-xs text-slate-500">(no database write)</span>
                        ) : null}
                        {event.error_class ? (
                          <Badge tone="muted" className="ml-0">
                            {event.error_class}
                          </Badge>
                        ) : null}
                        {driftCategory ? (
                          <Badge tone={driftCategory === "gipi_to_po_link" ? "warning" : "muted"}>
                            {driftFindingCategoryLabel(driftCategory)}
                          </Badge>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-600">
                      <div className="space-y-1">
                        <div>{event.error ?? "—"}</div>
                        {event.finding_hint ? (
                          <div className="rounded border border-sky-200 bg-sky-50/90 px-2 py-1.5 text-slate-800">
                            <div className="text-[0.7rem] font-semibold uppercase tracking-wide text-sky-900">
                              Hint — possible merge
                            </div>
                            <p className="mt-0.5 text-[0.7rem] leading-snug">{event.finding_hint.lead}</p>
                            <p className="mt-1 text-[0.7rem] leading-snug text-slate-600">{event.finding_hint.detail}</p>
                            {event.finding_hint.recommended_merge_targets.length > 0 ? (
                              <ul className="mt-1.5 list-inside list-disc text-[0.7rem] text-slate-800">
                                {event.finding_hint.recommended_merge_targets.map((target) => (
                                  <li key={target.target_mv_id}>
                                    Megaventory id {target.target_mv_id} — {target.candidate_name}{" "}
                                    <span className="whitespace-nowrap text-slate-500">
                                      (name similarity {(target.match_score * 100).toFixed(0)}%)
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-500">#{event.run_id}</td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setExpandedId(expandedId === event.id ? null : event.id)}>
                          {expandedId === event.id ? "Hide" : "Details"}
                        </Button>
                        {event.status === "failed" && event.mv_id !== INGEST_EVENT_RUN_LEVEL_MV_ID ? (
                          <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={DATA_CONNECTORS_READ_ONLY_TITLE}>
                            <Button
                              size="sm"
                              variant="secondary"
                              disabled={!hubWritesAllowed}
                              onClick={() => replayMutation.mutate({ entity: event.entity, mvIds: [event.mv_id] })}
                            >
                              Replay
                            </Button>
                          </ReadOnlyHoverLayer>
                        ) : null}
                        {isResolvableStatus(event.status) ? (
                          <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={DATA_CONNECTORS_READ_ONLY_TITLE}>
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={!hubWritesAllowed}
                              onClick={() => resolveMutation.mutate({ id: event.id })}
                            >
                              Resolve
                            </Button>
                          </ReadOnlyHoverLayer>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                  {expandedId === event.id ? (
                    <tr>
                      <td colSpan={8} className="bg-slate-50 px-4 py-3">
                        <div className="grid gap-3 lg:grid-cols-2">
                          <div>
                            {sweepNotes.length > 0 ? (
                              <div className="mb-3 rounded border border-indigo-200 bg-indigo-50/90 px-3 py-2 text-slate-800">
                                <div className="text-[0.7rem] font-semibold uppercase tracking-wide text-indigo-950">
                                  Sweep notes
                                </div>
                                <ul className="mt-1.5 list-inside list-disc text-[0.75rem] leading-snug text-slate-800">
                                  {sweepNotes.map((line) => (
                                    <li key={line}>{line}</li>
                                  ))}
                                </ul>
                              </div>
                            ) : null}
                            <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Diff</div>
                            <pre className="mt-1 max-h-72 overflow-auto rounded-md bg-white p-2 text-xs text-slate-800">
                              {event.diff ? JSON.stringify(event.diff, null, 2) : "—"}
                            </pre>
                          </div>
                          <div>
                            <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Payload</div>
                            <pre className="mt-1 max-h-72 overflow-auto rounded-md bg-white p-2 text-xs text-slate-800">
                              {event.payload ? JSON.stringify(event.payload, null, 2) : "—"}
                            </pre>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
              })}
            </tbody>
          </table>
        </CardBody>
      </Card>
    </div>
  );
}
